import type {
  ProductCatalogResponse,
  ProductListInterface,
} from "~~/server/types/Products";

export interface ProductCatalogRequest {
  page?: number;
  per_page?: number;
  category_slug?: string;
  q?: string;
  categories?: string;
  brands?: string;
  options?: string;
  min_price?: number;
  max_price?: number;
  sort?: string;
  include_facets?: boolean;
  include_product_options?: boolean;
  include_category_details?: boolean;
}

export const emptyProductCatalog = (perPage = 24): ProductCatalogResponse => ({
  list: [],
  product_options: [],
  meta: {
    current_page: 1,
    last_page: 1,
    per_page: perPage,
    total: 0,
    from: null,
    to: null,
  },
  facets: {
    brands: [],
    price: { min: 0, max: 0 },
    option_value_ids: [],
  },
  category: null,
});

export const fetchProductCatalog = async (
  request: ProductCatalogRequest,
): Promise<ProductCatalogResponse> => {
  const { $api } = useNuxtApp();

  return await $api<ProductCatalogResponse>("products/catalog", {
    method: "GET",
    query: request,
  });
};

export const fetchAllProductCatalog = async (
  request: Omit<ProductCatalogRequest, "page" | "per_page"> = {},
): Promise<ProductListInterface[]> => {
  const { $api } = useNuxtApp();
  const pageSize = 1000;
  const loadPage = (page: number) => $api<ProductCatalogResponse>(
    "products/catalog",
    {
      method: "GET",
      query: {
        ...request,
        page,
        per_page: pageSize,
        include_facets: false,
        include_product_options: false,
        include_category_details: false,
      },
    },
  );
  const firstPage = await loadPage(1);
  const products = [...firstPage.list];
  const remainingPages = Array.from(
    { length: Math.max(0, firstPage.meta.last_page - 1) },
    (_, index) => index + 2,
  );

  for (let offset = 0; offset < remainingPages.length; offset += 4) {
    const batch = await Promise.all(
      remainingPages.slice(offset, offset + 4).map(loadPage),
    );

    products.push(...batch.flatMap(result => result.list));
  }

  return products;
};
